import { AbstractCommand } from '../AbstractCommand';

import type { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import type { VideoEditor } from '../../VideoEditor';

/**
 * ShowInKaraokeCommand
 * Removes the provided transcript token ids from the hiddenTranscriptTokenIds set
 * of any karaoke clips that currently contain them. Undo restores previous
 * hidden lists per clip (just like HideInKaraokeCommand does when adding).
 */
export class ShowInKaraokeCommand extends AbstractCommand {
  private transcriptTokenIds: string[];
  private backedUpClipHiddenTokenIds: Map<
    string,
    KaraokeClip['hiddenTranscriptTokenIds']
  > = new Map();
  private backedUpClipIgnoredByFiltersIds: Map<
    string,
    KaraokeClip['ignoredByFiltersIds']
  > = new Map();

  constructor(editor: VideoEditor, transcriptTokenIds: string[]) {
    super(editor);
    this.transcriptTokenIds = transcriptTokenIds;
  }

  execute(): boolean {
    if (!this.transcriptTokenIds) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    let didModify = false;

    for (let i = 0; i < clips.length; i++) {
      const clip = clips[i];
      if (this.isManualFilterRestore(clip)) {
        this.backupClipIgnoredByFiltersIds(clip);
        this.updateClipIgnoredByFilterIds(clip);
        didModify = true;
      }
      const clipTokensToShow = this.getClipTokensToShow(clip);
      if (clipTokensToShow.length) {
        this.backupClipHiddenTokenIds(clip);
        this.updateClipHiddenTokenIdsWith(clip, clipTokensToShow);
        didModify = true;
      }
    }

    if (!didModify) return true; // structural no-op, still success per current command patterns

    this.editor.markDirty('show_in_karaoke');
    return true;
  }

  undo(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    const indexById = new Map<string, number>();
    for (let i = 0; i < clips.length; i++) {
      indexById.set(clips[i].id, i);
    }

    for (const [clipId, hiddenTranscriptTokenIds] of this
      .backedUpClipHiddenTokenIds) {
      const index = indexById.get(clipId);
      if (index !== undefined && clips[index]) {
        clips[index].setHiddenTranscriptTokenIds(hiddenTranscriptTokenIds);
      }
    }

    for (const [clipId, ignoredIds] of this.backedUpClipIgnoredByFiltersIds) {
      const index = indexById.get(clipId);
      if (index !== undefined && clips[index]) {
        clips[index].setIgnoredByFiltersIds(ignoredIds);
      }
    }

    this.editor.markDirty('show_in_karaoke_undo');
    return true;
  }

  getName(): string {
    return 'ShowInKaraokeCommand';
  }

  getDescription(): string {
    return 'Shows (unhides) specified transcript tokens within karaoke clips';
  }

  /**
   * when users wants to override tokens that were hidden by karaoke config filtering
   */
  private isManualFilterRestore(clip: KaraokeClip): boolean {
    const { hideFillers, hideComma, hidePeriod } = clip.karaokeConfig;
    return (
      // some of the filtering is on
      (hideFillers || hideComma || hidePeriod) &&
      // every token to show is on the current clip
      // and every token would be removed by karaoke config filtering
      this.transcriptTokenIds.every(id => {
        const allTokens = clip.transcriptTokens;
        const tokenIdx = allTokens.findIndex(t => t.id === id);
        return (
          tokenIdx !== -1 &&
          !clip.filterByKaraokeConfig(allTokens[tokenIdx], tokenIdx, allTokens)
        );
      })
    );
  }

  private getClipTokensToShow(clip: KaraokeClip): string[] {
    const tokensToShowSet = new Set(this.transcriptTokenIds);
    return clip.hiddenTranscriptTokenIds.filter(id => tokensToShowSet.has(id));
  }

  private updateClipHiddenTokenIdsWith(
    clip: KaraokeClip,
    clipTokensToShow: string[],
  ): void {
    const clipTokensToShowSet = new Set(clipTokensToShow);
    clip.setHiddenTranscriptTokenIds(
      clip.hiddenTranscriptTokenIds.filter(id => !clipTokensToShowSet.has(id)),
    );
  }

  private updateClipIgnoredByFilterIds(clip: KaraokeClip): void {
    clip.setIgnoredByFiltersIds([
      ...new Set([...clip.ignoredByFiltersIds, ...this.transcriptTokenIds]),
    ]);
  }

  private backupClipHiddenTokenIds(clip: KaraokeClip): void {
    this.backedUpClipHiddenTokenIds.set(clip.id, [
      ...clip.hiddenTranscriptTokenIds,
    ]);
  }

  private backupClipIgnoredByFiltersIds(clip: KaraokeClip): void {
    this.backedUpClipIgnoredByFiltersIds.set(clip.id, [
      ...clip.ignoredByFiltersIds,
    ]);
  }
}
